/**
 * Tables out of the console, as files a spreadsheet opens.
 *
 * Built in the browser from what the API already returns, rather than by a
 * server route that streams a file: every row here is one the operator could
 * already read on screen, so there is no second path to the data that would
 * need its own access rules.
 */

export type Cell = string | number | null | undefined;

/**
 * One cell, quoted only when it has to be.
 *
 * A cell a customer wrote that starts with `=`, `+`, `-` or `@` is a formula
 * to Excel, and "=HYPERLINK(…)" in a visitor's name would run on the
 * operator's machine when the export is opened. Such cells get a leading
 * apostrophe, which a spreadsheet shows as text and nothing else.
 */
export function csvCell(value: Cell): string {
  if (value === null || value === undefined) return "";
  let text = String(value);
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(rows: Cell[][]): string {
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}

/**
 * Hands a CSV to the browser as a download.
 *
 * The byte-order mark is for Excel, which otherwise reads UTF-8 as the local
 * code page and turns "Ayşe" into "AyÅŸe".
 */
export function downloadCsv(fileName: string, rows: Cell[][]): void {
  const blob = new Blob(["﻿", toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked a moment later: some browsers start the download after the click returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "2026-09-30" for a file name, in the operator's own day. */
export function fileStamp(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
